import { BrowserStorageLocalService } from "./../../../services/shared/BrowserStorageServices/BrowserStorageLocalService.service";
import { Injectable, effect, untracked } from "@angular/core";
// Translate
import { TranslateService } from "@ngx-translate/core";
// Own services
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
// Interfaces
import {
  CUPS_COLUMNS_INDEX,
  EntityDefinition,
} from "../../../interfaces/CupsGlobalInterface.type";
import type { EntityDefinitionsParsed } from "../../../interfaces/CupsGlobalInterface.type";
import { Agrupation } from "../../../interfaces/AgrupationGlobalInterface.type";
import { Entity } from "../../../interfaces/EntityGlobalInterface.type";
// Variables
import { MENU, MenuItem } from "./navbar-menu";
import { NAVBAR_SHORTCUTS } from "./navbar-shortcuts";
import { PROFILES } from "../../../../assets/profiles/profiles";
import { ENVIRONMENT } from "../../../../environments/environment";
import { Client } from "../../../interfaces/ClientGlobalInterface.type";

/** Builds the filtered navbar menu from session state, excluding CUPS and custom-data links in presentation mode. */
@Injectable({
  providedIn: "root"
})
export class NavbarMenuService {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/
  sessionProfile: string;
  currentEntity: Entity;
  entityDefinitionData: EntityDefinitionsParsed;
  cups: EntityDefinition;
  currentAgrupation: Agrupation;
  currentClient: Client;

  cupsDependentMenus = ["cups-info", "not-cups-assigned-devices"];

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/
  constructor(
    private readonly BrowserStorageLocalService: BrowserStorageLocalService,
    private readonly SessionDataService: SessionDataService,
    private readonly translate: TranslateService
  ) {
    effect(() => {
      const entityDefinitionData = this.SessionDataService.entityCupsConfParsed();
      if (entityDefinitionData !== null) {
        this.handleEntityCupsConfChange(entityDefinitionData);
      }
    });
    effect(() => {
      const profile = this.SessionDataService.profile();
      if (profile) {
        this.handleProfileChange(profile);
      }
    });
    effect(() => {
      const agrupation = this.SessionDataService.agrupation();
      if (agrupation !== null) {
        this.handleAgrupationChange(agrupation);
      }
    });
    effect(() => {
      const entity = this.SessionDataService.entity();
      if (entity !== null) {
        this.handleEntityChange(entity);
      }
    });
    effect(() => {
      const client = this.SessionDataService.client();
      if (client !== null) {
        this.handleClientChange(client);
      }
    });
    effect(() => {
      const mainPage = this.SessionDataService.mainPage();
      if (mainPage !== null) {
        this.sendUpdatedMenu();
      }
    });
    effect(() => {
      this.SessionDataService.presentationModeActive();
      if (this.sessionProfile) {
        this.sendUpdatedMenu();
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Handle CUPS config changes
  /***************************************************************************/
  private handleEntityCupsConfChange(entityDefinitionData: EntityDefinitionsParsed): void {
    this.entityDefinitionData = entityDefinitionData;
    this.cups = this.entityDefinitionData?.entityCups;
    this.sendUpdatedMenu();
  }

  /***************************************************************************/
  // ANCHOR Handle profile changes
  /***************************************************************************/
  private handleProfileChange(sessionProfile: string): void {
    this.sessionProfile = sessionProfile;
    this.sendUpdatedMenu();
  }

  /***************************************************************************/
  // ANCHOR Handle group changes
  /***************************************************************************/
  private handleAgrupationChange(agrupation: Agrupation): void {
    this.currentAgrupation = agrupation;
    this.sendUpdatedMenu();
  }

  /***************************************************************************/
  // ANCHOR Handle entity changes
  /***************************************************************************/
  private handleEntityChange(entity: Entity): void {
    this.currentEntity = entity;
    this.sendUpdatedMenu();
  }

  /***************************************************************************/
  // ANCHOR Handle client changes
  /***************************************************************************/

  private handleClientChange(client: Client): void {
    this.currentClient = client;
    this.sendUpdatedMenu();
  }

  /***************************************************************************/
  // ANCHOR Send updated menu
  /***************************************************************************/
  private sendUpdatedMenu(): void {
    this.SessionDataService.sendNavbarMenu(
      this.setMenu(
        this.cups,
        this.entityDefinitionData,
        this.sessionProfile,
        this.currentAgrupation,
        this.currentEntity
      )
    );
  }

  /***************************************************************************/
  // ANCHOR Menu initialization
  /***************************************************************************/
  loadMenu(): void {
    this.sessionProfile = untracked(() => this.SessionDataService.profile());
    this.currentEntity = untracked(() => this.SessionDataService.entity());
    this.currentAgrupation = untracked(() => this.SessionDataService.agrupation());
    this.entityDefinitionData = untracked(() => this.SessionDataService.entityCupsConfParsed());
    this.cups = this.entityDefinitionData?.entityCups;
    this.sendUpdatedMenu();
  }

  /***************************************************************************/
  // ANCHOR Menu setup
  /***************************************************************************/
  setMenu(
    cups: EntityDefinition,
    entityDefinitionData: EntityDefinitionsParsed,
    sessionProfile: string,
    agrupation: Agrupation,
    entity: Entity
  ): MenuItem[] {
    let menuList: MenuItem[] = [];
    MENU.forEach(menuItem => {
      this.setMenuItem(
        menuList,
        menuItem,
        cups,
        entityDefinitionData,
        sessionProfile,
        agrupation,
        entity
      );
    });
    return menuList;
  }

  getUserMainPage(): string | null {
    const configData = this.BrowserStorageLocalService.getJsonValue("config");
    const currentUser = untracked(() => this.SessionDataService.user());
    if (configData?.length > 0) {
      const userConfig = configData.find((config: any) => config.user === currentUser);
      return userConfig?.mainPage || null;
    }
    return null;
  }
  /***************************************************************************/
  // ANCHOR Menu item setup
  /***************************************************************************/
  private setMenuItem(
    menuList: any[],
    menuItem: any,
    cups: EntityDefinition,
    entityDefinitionData: EntityDefinitionsParsed,
    sessionProfile: string,
    agrupation: Agrupation,
    entity: Entity
  ): void {
    // Check profile and client permissions
    if (
      this.isProfileAllowed(menuItem, sessionProfile) &&
      this.isClientAllowed(menuItem, entity) &&
      this.shouldIncludeMenuItem(menuItem, cups)
    ) {
      let newMenuItem: MenuItem = this.createMenuItem(menuItem, cups, sessionProfile, entity);
      this.applyMenuItemVisibility(menuItem, newMenuItem, entityDefinitionData);
      this.addSubMenuItems(
        newMenuItem,
        menuItem,
        cups,
        entityDefinitionData,
        sessionProfile,
        agrupation,
        entity
      );
      this.pushMenuItemIfVisible(menuList, newMenuItem);
    }
  }

  /***************************************************************************/
  // ANCHOR Decide whether to include menu item
  /***************************************************************************/
  private shouldIncludeMenuItem(menuItem: any, cups: EntityDefinition): boolean {
    if (
      this.SessionDataService.presentationModeActive() &&
      this.isPresentationModeHiddenMenuItem(menuItem)
    ) {
      return false;
    }

    return (
      !this.cupsDependentMenus.includes(menuItem.name) ||
      (this.cupsDependentMenus.includes(menuItem.name) && Boolean(cups))
    );
  }

  private isPresentationModeHiddenMenuItem(menuItem: any): boolean {
    const menuName = typeof menuItem.name === "string" ? menuItem.name.toLowerCase() : "";
    const menuLink = typeof menuItem.link === "string" ? menuItem.link.toLowerCase() : "";
    return menuName === "custom-data" || menuName.includes("cups") || menuLink.includes("/cups");
  }

  /***************************************************************************/
  // ANCHOR Create menu item
  /***************************************************************************/
  private createMenuItem(
    menuItem: any,
    cups: EntityDefinition,
    sessionProfile: string,
    entity: Entity
  ): MenuItem {
    let newMenuItem: MenuItem = new MenuItem();
    newMenuItem.name = this.getMenuItemName(menuItem, cups);
    newMenuItem.link = menuItem.link;
    newMenuItem.icon = menuItem.icon;
    newMenuItem.subicon = menuItem.subicon;
    newMenuItem.hideForDemo = (menuItem.hideForDemo && sessionProfile !== PROFILES.ARSON) || false;
    newMenuItem.profile = menuItem.profile?.length > 0 ? menuItem.profile : [sessionProfile]; // Unrestricted items fall back to the session profile
    newMenuItem.client = menuItem.client?.length > 0 ? menuItem.client : [entity?.client]; // Unrestricted items fall back to the current entity's client
    newMenuItem.submenu = menuItem.submenu?.length > 0 ? [] : null;
    newMenuItem.shortcut = NAVBAR_SHORTCUTS[menuItem.link] ? NAVBAR_SHORTCUTS[menuItem.link] : null;
    this.applyMenuItemDisabledState(
      menuItem,
      newMenuItem,
      cups,
      untracked(() => this.SessionDataService.entityCupsConfParsed())
    );
    return newMenuItem;
  }

  /***************************************************************************/
  // ANCHOR Get menu item name
  /***************************************************************************/
  private getMenuItemName(menuItem: any, cups: EntityDefinition): string {
    if (menuItem.name === "cups-info") {
      return cups?.name || "CUPS";
    }
    if (menuItem.name === "not-cups-assigned-devices") {
      return this.translate.instant("devices-without") + " " + (cups?.name || "CUPS");
    }
    if (menuItem.name === "cups-list") {
      return this.translate.instant("list-of") + " " + (cups?.name || "CUPS");
    }
    if (menuItem.name === "meter-in-cups-installation-list") {
      return this.translate.instant("meter-in-cups-installation-list", {
        cupsName: cups?.name || "CUPS"
      });
    }
    if (menuItem.name === "cups") {
      return cups?.name || "CUPS";
    }
    if (
      menuItem.name === "gateways-client" &&
      (this.sessionProfile === PROFILES.ARSON || this.sessionProfile === PROFILES.USUARIO_ARSON)
    ) {
      return this.translate.instant("gateways") + " (" + this.translate.instant("client") + ")";
    }
    if (
      menuItem.name === "users-client" &&
      this.sessionProfile !== PROFILES.ARSON &&
      this.sessionProfile !== PROFILES.USUARIO_ARSON
    ) {
      return this.translate.instant("users");
    }
    return menuItem.name;
  }

  /***************************************************************************/
  // ANCHOR Check whether profile is allowed for item
  /***************************************************************************/
  private isProfileAllowed(menuItem: any, sessionProfile: string): boolean {
    return menuItem.profile?.length > 0 ? menuItem.profile.includes(sessionProfile) : true;
  }

  /***************************************************************************/
  // ANCHOR Check whether client is allowed for item
  /***************************************************************************/
  private isClientAllowed(menuItem: any, entity: Entity): boolean {
    return menuItem.client?.length > 0 ? menuItem.client.includes(entity?.client) : true;
  }

  /***************************************************************************/
  // ANCHOR Apply disabled state
  /***************************************************************************/
  private applyMenuItemDisabledState(
    menuItem: any,
    newMenuItem: MenuItem,
    cups: EntityDefinition,
    entityDefinitionData: EntityDefinitionsParsed
  ): void {
    switch (menuItem.disabled) {
      case "cups":
        newMenuItem.disabled = cups == null;
        break;
      case "entityCupsConf":
        newMenuItem.disabled = !entityDefinitionData?.entityCups;
        break;
      case "!extraData":
        newMenuItem.disabled =
          !entityDefinitionData || !entityDefinitionData?.entityCupsColumns?.length;
        break;
      case "virtual":
        newMenuItem.disabled = this.currentAgrupation?.virtual;
        break;
      case "isTlm":
        newMenuItem.disabled =
          this.sessionProfile !== PROFILES.ARSON &&
          this.sessionProfile !== PROFILES.USUARIO_ARSON &&
          this.sessionProfile !== PROFILES.ADMIN_CLIENTE &&
          this.sessionProfile !== PROFILES.USUARIO_TELEMEDIDA &&
          !untracked(() => this.SessionDataService.telemetryActive());
        break;
      case "disabled":
        newMenuItem.disabled = true;
        break;
      default:
        break;
    }
  }

  /***************************************************************************/
  // ANCHOR Apply menu item visibility
  /***************************************************************************/
  private applyMenuItemVisibility(
    menuItem: any,
    newMenuItem: MenuItem,
    entityDefinitionData: EntityDefinitionsParsed
  ): void {
    if (menuItem.visible) {
      switch (menuItem.visible) {
        case "gas":
          newMenuItem.hidden = !untracked(() => this.SessionDataService.gasDay());
          break;
        case "!entityCupsConf":
          newMenuItem.hidden = !!entityDefinitionData?.entityCups;
          break;
        case "entityCupsConf":
          newMenuItem.hidden = !entityDefinitionData?.entityCups;
          break;
        case "globalManagement":
          newMenuItem.hidden = !untracked(() => this.SessionDataService.globalManagement());
          break;
        case "!globalManagement":
          newMenuItem.hidden = untracked(() => this.SessionDataService.globalManagement());
          break;
        default:
          newMenuItem.hidden = false;
          break;
      }
    }
  }

  /***************************************************************************/
  // ANCHOR Add submenus
  /***************************************************************************/
  private addSubMenuItems(
    newMenuItem: MenuItem,
    menuItem: any,
    cups: EntityDefinition,
    entityDefinitionData: EntityDefinitionsParsed,
    sessionProfile: string,
    agrupation: Agrupation,
    entity: Entity
  ): void {
    if (menuItem.submenu) {
      menuItem.submenu.forEach(submenuItem => {
        this.setMenuItem(
          newMenuItem.submenu,
          submenuItem,
          cups,
          entityDefinitionData,
          sessionProfile,
          agrupation,
          entity
        );
      });
    }
  }

  /***************************************************************************/
  // ANCHOR Add menu item if visible
  /***************************************************************************/
  private pushMenuItemIfVisible(menuList: any[], newMenuItem: MenuItem): void {
    if (newMenuItem.visible !== false) {
      if (newMenuItem.hideForDemo && ENVIRONMENT.demo && this.sessionProfile !== PROFILES.ARSON) {
        newMenuItem.disabled = true;
      }
      menuList.push(newMenuItem);
    }
  }
}
